"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import Logo from "@/../public/assets/images/logoWithoutTxt.svg";

type PreloaderProps = {
  durationMs?: number;
};

export default function Preloader({ durationMs = 1000 }: PreloaderProps) {
  const [isVisible, setIsVisible] = useState(true);
  const [isFading, setIsFading] = useState(false);

  useEffect(() => {
    const startFadeTimerId = window.setTimeout(() => setIsFading(true), durationMs);
    const removeTimerId = window.setTimeout(() => setIsVisible(false), durationMs + 500);
    return () => {
      window.clearTimeout(startFadeTimerId);
      window.clearTimeout(removeTimerId);
    };
  }, [durationMs]);

  if (!isVisible) return null;

  return (
    <div
      className={`fixed inset-0 z-[1000] flex items-center justify-center bg-black transition-opacity duration-500 ${
        isFading ? "opacity-0" : "opacity-100"
      }`}
      aria-label="Loading"
      role="status"
    >
      <div
        className="relative flex items-center justify-center logo-zoom"
        style={{ width: 140, height: 100, transform: "scale(0.9)", opacity: 0.6, animationDuration: `${durationMs}ms` }}
      >
        <Image src={Logo} alt="Nimbus Node" width={120} height={80} priority />

        {/* Bubble animation overlay */}
        <div className="pointer-events-none absolute inset-0 overflow-visible">
          {[
            { left: "15%", size: 6, delay: 0 },
            { left: "35%", size: 8, delay: 200 },
            { left: "55%", size: 5, delay: 400 },
            { left: "75%", size: 7, delay: 600 },
            { left: "25%", size: 4, delay: 800 },
            { left: "65%", size: 6, delay: 1000 },
          ].map((b, idx) => (
            <span
              key={idx}
              className="bubble"
              style={{ left: b.left, width: b.size, height: b.size, animationDelay: `${b.delay}ms` }}
            />
          ))}
        </div>
      </div>

      <style jsx>{`
        @keyframes zoomIn {
          0% { transform: scale(0.9); opacity: 0.6; }
          100% { transform: scale(1); opacity: 1; }
        }
        .logo-zoom {
          animation-name: zoomIn;
          animation-timing-function: cubic-bezier(0.22, 1, 0.36, 1);
          animation-fill-mode: forwards;
          will-change: transform, opacity;
        }
        @keyframes rise {
          0% { transform: translateY(20%); opacity: 0; }
          20% { opacity: 1; }
          100% { transform: translateY(-90%); opacity: 0; }
        }
        .bubble {
          position: absolute;
          bottom: 0;
          display: block;
          background: rgba(255, 255, 255, 0.85);
          border-radius: 9999px;
          animation: rise 2.4s linear infinite;
          will-change: transform, opacity;
          filter: drop-shadow(0 0 6px rgba(255,255,255,0.3));
        }
        @media (prefers-reduced-motion: reduce) {
          .bubble { display: none; }
        }
      `}</style>
    </div>
  );
}


